/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { DefaultButton, IIconProps, PrimaryButton, Stack, Text } from "@fluentui/react";

import { RoleNamesEnum, RolesEnum } from "Constants/RolesPermissionsConstants";
import { IUserRole } from "Interfaces/AccessControl/IUserRole";

import {
  buttonStyles,
  containerStyles,
  deleteButtonStyles,
  horizontalGapStackTokens,
  stackRoleDetailsStyles,
  stackRoleStyles,
  textStyles,
} from "./AccessControlRole.styles";
import { IAccessControlRoleProps } from "./AccessControlRoleProps";

const editIcon: IIconProps = { iconName: "Edit" };
const deleteIcon: IIconProps = { iconName: "Delete" };

export const AccessControlRole = (props: IAccessControlRoleProps): React.ReactElement => {
  const { role, actions } = props;

  const canEdit = !role.userRoleName.includes("program-owner");
  const roleName = role.userRoleName
    .substring(role.userRoleName.indexOf("-"))
    .replace(/-/g, " ")
    .trim()
    // Workaround for rename of Tech Area Validator to Product Group Partner, ideally we should map the DB Role Names to the Display Names.
    .replace("tech area validator", RoleNamesEnum.PRODUCT_GROUP_PARTNER)
    // Workaround for rename of MVP Reviewer to Community Manager
    .replace(
      "reviewer",
      role.userRoleName == RolesEnum.MVP_REVIEWER ? RoleNamesEnum.COMMUNITY_MANAGER : "reviewer"
    );

  const roleDetails = (role: IUserRole) => {
    if (role.technologyAreas?.length > 0) {
      const technologyAreas = role.technologyAreas.map((ta) => ta.split("__")[0]);
      return technologyAreas.join(", ");
    }

    if (role.awardCategories?.length > 0) {
      const awardCategories = role.awardCategories.map((ac) => ac.split("__")[0]);
      return awardCategories.join(", ");
    }

    if (role.userRegions?.length > 0) {
      return role.userRegions
        .map((ur) => {
          if (ur.stateOrProvinceName && ur.stateOrProvinceName !== "")
            return ur.stateOrProvinceName;
          if (ur.countryOrRegionName && ur.countryOrRegionName !== "")
            return ur.countryOrRegionName;
          if (ur.clRegionName && ur.clRegionName !== "") return ur.clRegionName;
        })
        .join(", ");
    }

    return "";
  };

  const confirmDeletingRole = () => {
    actions.confirmDeletingRole(role.id);
  };

  return (
    <>
      <Stack
        styles={containerStyles}
        horizontalAlign="space-between"
        tokens={horizontalGapStackTokens}
        horizontal
      >
        <Stack.Item grow={2} styles={stackRoleStyles}>
          <Text styles={textStyles}>{roleName}</Text>
        </Stack.Item>
        <Stack.Item grow={2} styles={stackRoleDetailsStyles}>
          <Text styles={textStyles}>{roleDetails(role)}</Text>
        </Stack.Item>
        <Stack.Item grow={3}>
          <PrimaryButton
            iconProps={editIcon}
            styles={buttonStyles}
            disabled={!canEdit}
            onClick={(): void => {
              actions.editRole(role);
            }}
          >
            Edit
          </PrimaryButton>
          <DefaultButton
            iconProps={deleteIcon}
            styles={deleteButtonStyles}
            onClick={confirmDeletingRole}
            disabled={!canEdit}
          >
            Delete
          </DefaultButton>
        </Stack.Item>
      </Stack>
    </>
  );
};
